import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaTimes, FaFilter } from "react-icons/fa";
import useDocumentMeta from "../hooks/useDocumentMeta";

const galleryItems = [
    {
        src: "/assets/images/prod1.jpeg",
        category: "Products"
    },
    {
        src: "/assets/images/prod2.jpeg",
        category: "Products"
    },
    {
        src: "/assets/images/prod3.jpeg",
        category: "Products"
    },
    {
        src: "/assets/images/prod4.jpeg",
        category: "Products"
    },
    {
        src: "/assets/images/prod5.jpeg",
        category: "Products"
    },
    {
        src: "/assets/images/gallery-6.jpg",
        category: "Packaging"
    },
    {
        src: "/assets/images/prod6.jpeg",
        category: "Packaging"
    },
    {
        src: "/assets/images/gallery-14.jpg",
        category: "Packaging"
    },
    {
        src: "/assets/images/gallery-22.jpg",
        category: "Packaging"
    }
];

export default function GalleryPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null);
    const [activeCategory, setActiveCategory] = useState("All");

    useDocumentMeta({
        title: t("seo.gallery.title"),
        description: t("seo.gallery.description"),
        canonicalPath: "/gallery",
        robots: "index,follow,max-image-preview:none",
    });

    // Keep original English category names for filtering logic
    const categoryKeys = ["All", "Products", "Facilities", "Production", "Packaging", "Team"];

    // Create translated display names for UI
    const categories = categoryKeys.map(key => {
        if (key === "All") return t('gallery.categories.all');
        return t(`gallery.categories.${key.toLowerCase()}`);
    });

    const filteredItems = activeCategory === "All"
        ? galleryItems
        : galleryItems.filter(item => item.category === activeCategory);

    return (
        <main className="bg-vsblack text-white min-h-screen pt-20">
            {/* Header */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-5xl md:text-6xl font-bold font-serif mb-6">
                            <span className="text-vsred hero-text-shadow">{t('gallery.title')}</span>
                        </h1>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            {t('gallery.description')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-8 px-6 bg-vsblackLight sticky top-20 z-40 border-y border-gray-800/50 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center gap-4 overflow-x-auto pb-2">
                        <FaFilter className="text-vsred flex-shrink-0" />
                        {categories.map((category, index) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(categoryKeys[index])}
                                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all duration-300 ${activeCategory === categoryKeys[index]
                                    ? "bg-gradient-to-br from-vsred to-vsred-dark text-white shadow-red-glow"
                                    : "bg-vsblackLight text-gray-400 hover:text-white border border-gray-800 hover:border-vsred/50"
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallery Grid */}
            <section className="py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        layout
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                    >
                        {filteredItems.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={{ y: -8 }}
                                className="relative group cursor-pointer overflow-hidden rounded-xl shadow-dark"
                                onClick={() => setSelectedImage(item)}
                            >
                                {/* Image */}
                                <div className="relative h-64 overflow-hidden bg-vsblackLight">
                                    <img
                                        src={item.src}
                                        alt={item.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />

                                    {/* Overlay gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

                                    {/* Category badge */}
                                    <div className="absolute top-4 left-4 px-3 py-1 bg-vsred/90 backdrop-blur-sm rounded-full text-xs font-semibold text-white">
                                        {item.category}
                                    </div>

                                    {/* Title overlay */}
                                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                                        <h3 className="text-xl font-bold text-white font-serif mb-1">
                                            {item.title}
                                        </h3>
                                        <div className="flex items-center gap-2 text-sm text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <span>Click to enlarge</span>
                                            <span>→</span>
                                        </div>
                                    </div>

                                    {/* Red glow effect on hover */}
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                        <div className="absolute inset-0 shadow-red-glow" />
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        <AnimatePresence>
                            {selectedImage && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md overflow-auto"
                                    onClick={() => setSelectedImage(null)}
                                >
                                    {/* Close Button */}
                                    <motion.button
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        className="absolute top-6 right-6 w-12 h-12 bg-vsred/90 hover:bg-vsred rounded-full flex items-center justify-center text-white text-xl z-20"
                                        onClick={() => setSelectedImage(null)}
                                    >
                                        <FaTimes />
                                    </motion.button>

                                    {/* Modal Content */}
                                    <motion.div
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0.9, opacity: 0 }}
                                        className="relative max-w-3xl w-full bg-vsblack rounded-xl shadow-lg p-4 flex flex-col items-center"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {/* Image */}
                                        <img
                                            src={selectedImage.src}
                                            alt={selectedImage.title}
                                            className="w-full h-auto max-h-[50vh] object-contain rounded-xl"
                                        />

                                        {/* Title & Category */}
                                        <div className="mt-4 text-center px-4">
                                            <h3 className="text-2xl font-bold text-white font-serif mb-1">
                                                {selectedImage.title}
                                            </h3>
                                            <p className="text-gray-400">{selectedImage.category}</p>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                    </motion.div>

                    {/* No results message */}
                    {filteredItems.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-20"
                        >
                            <p className="text-gray-400 text-lg">{t('gallery.noResults')}</p>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {selectedImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md"
                        onClick={() => setSelectedImage(null)}
                    >
                        {/* Close button */}
                        <motion.button
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            className="absolute top-6 right-6 w-12 h-12 bg-vsred/90 hover:bg-vsred rounded-full flex items-center justify-center text-white text-xl transition-colors shadow-red-glow z-10"
                            onClick={() => setSelectedImage(null)}
                        >
                            <FaTimes />
                        </motion.button>

                        {/* Image content */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            className="max-w-6xl w-full flex justify-center items-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="card-dark overflow-hidden max-h-[90vh] w-full">
                                <img
                                    src={selectedImage.src}
                                    alt={selectedImage.title}
                                    className="w-full h-auto max-h-[80vh] object-contain mx-auto"
                                />
                                <div className="p-8">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="px-3 py-1 bg-vsred/20 border border-vsred/50 rounded-full text-xs font-semibold text-vsred-light">
                                            {selectedImage.category}
                                        </span>
                                    </div>
                                    <h3 className="text-3xl font-bold text-white font-serif mb-3">
                                        {selectedImage.title}
                                    </h3>
                                    <p className="text-gray-400 text-lg">
                                        {selectedImage.description}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* CTA Section */}
            <section className="py-20 px-6 bg-gradient-to-b from-vsblack to-vsblackLight">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="card-dark p-12"
                    >
                        <h2 className="text-3xl font-bold font-serif mb-4 text-white">
                            {t('gallery.cta.title')}
                        </h2>
                        <p className="text-gray-400 mb-8 text-lg">
                            {t('gallery.cta.description')}
                        </p>
                        <a
                            href="/#quote"
                            className="btn-primary inline-block text-lg"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/');
                                setTimeout(() => {
                                    document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                            }}
                        >
                            {t('nav.requestQuote')}
                        </a>
                    </motion.div>
                </div>
            </section>
        </main>
    );
}

